// Coleção: o ACERVO cruzado com o que o aluno tem. Funções puras, sem React e sem
// conhecer o AVA nem bioma nenhum — recebem o acervo por parâmetro, como
// `album.js` recebe o conteúdo.
//
// A DIFERENÇA ENTRE ISTO E `album.js` É O QUE CADA UM MOSTRA, e ela é decisão de
// produto (D12 do Plano_Trilha_Persistencia_e_Regioes.md):
//
//   album.js   o que o aluno ganhou NAQUELA trilha — derivado das fases concluídas,
//              e é o que a tela de encerramento celebra;
//   colecao.js o ACERVO COMPLETO com os buracos à vista, atravessando regiões e
//              trilhas — é onde o aluno descobre que existe mais para colecionar.
//
// Por isso aqui a fonte é a coleção GRAVADA no servidor
// (`activity_ludic_trail_collection`, via `GET activity/ludic-trail/collection`) e
// não uma derivação: as cartas vieram de atividades que já terminaram, algumas de
// outras trilhas, e não há fase nenhuma em mão para derivar delas.

/**
 * O acervo com cada carta marcada como possuída ou não, região por região.
 *
 * As regiões vêm TODAS, inclusive as em que o aluno não tem nada — é justamente a
 * região vazia que informa que existe outro bioma para explorar. Filtrar as vazias
 * pareceria mais limpo e apagaria a única coisa que a tela tem para dizer a quem
 * está começando.
 *
 * @param {Array}  acervo    de `conteudo/acervoDeCartas()`
 * @param {object} possuidos mapa cardId → data de conquista (string ou null)
 * @returns {Array} regiões com `cartas`, `total` e `conquistadas`
 */
export function colecaoPorRegiao(acervo = [], possuidos = {}) {
  return acervo.map(regiao => {
    const cartas = (regiao.cartas || []).map(carta => {
      // `hasOwnProperty` e não `possuidos[id] ? ...`: o servidor pode devolver
      // `obtainedAt` vazio (a coluna é preenchida por `now()`, mas um registro
      // antigo ou uma migração podem não ter data), e ter a carta sem saber quando
      // continua sendo TER A CARTA. Com verdade/falsidade do valor, essa carta
      // apareceria como silhueta para quem já a conquistou.
      const possuida = Object.prototype.hasOwnProperty.call(
        possuidos,
        carta.id
      );
      return {
        ...carta,
        possuida,
        obtidaEm: possuida ? possuidos[carta.id] || null : null
      };
    });

    return {
      ...regiao,
      cartas,
      total: cartas.length,
      conquistadas: cartas.filter(carta => carta.possuida).length
    };
  });
}

/**
 * Quanto do acervo INTEIRO o aluno tem.
 *
 * Some as regiões em vez de contar a resposta do servidor: o que o aluno tem pode
 * incluir carta de id que o catálogo não conhece mais (ver `cartasOrfas`), e essa
 * carta não tem lugar na tela — contá-la no resumo daria um "23 de 22".
 */
export function resumoDaColecao(colecao = []) {
  return colecao.reduce(
    (soma, regiao) => ({
      total: soma.total + (regiao.total || 0),
      conquistadas: soma.conquistadas + (regiao.conquistadas || 0)
    }),
    { total: 0, conquistadas: 0 }
  );
}

/**
 * Ids que o aluno tem e o catálogo não conhece.
 *
 * Existe para DEIXAR RASTRO, não para tratar caso: acontece quando um `card_id` é
 * renomeado depois de a coleção existir, que é a única operação destrutiva do
 * módulo sem conserto (ver `conteudo/contrato.js`). A carta desaparece da tela sem
 * erro nenhum, e o aluno é quem sente — ele perdeu uma carta. Quem chama isto
 * avisa no console; nada na interface depende disso.
 *
 * @returns {string[]} os ids órfãos
 */
export function cartasOrfas(acervo = [], possuidos = {}) {
  const conhecidos = new Set();
  acervo.forEach(regiao => {
    (regiao.cartas || []).forEach(carta => conhecidos.add(carta.id));
  });

  return Object.keys(possuidos).filter(id => !conhecidos.has(id));
}

/**
 * A fração conquistada, de 0 a 100, arredondada.
 *
 * NUNCA DEVOLVE 100 SEM A COLEÇÃO ESTAR COMPLETA, e é por isso que arredonda para
 * BAIXO: com `Math.round`, 43 de 44 daria 98% mas numa região grande o aluno veria
 * "100%" faltando uma carta. Barra cheia com buraco na grade é a interface se
 * contradizendo na mesma tela.
 *
 * E NUNCA DEVOLVE 0 COM UMA CARTA NA MÃO, pelo motivo simétrico: 1 de 110 dá 0,9%,
 * que o piso arredondaria para nada, e a primeira carta do aluno não pode deixar a
 * barra do jeito que ela estava. O piso de 1% vale só a partir da primeira carta —
 * o `Math.max` sozinho fazia zero virar 1%, e a barra nascia com um traço aceso para
 * quem nunca jogou (pego pelo teste, e é o caso mais comum do AVA).
 */
export function percentualDaColecao(conquistadas, total) {
  if (!total || total <= 0) return 0;
  if (!conquistadas || conquistadas <= 0) return 0;
  if (conquistadas >= total) return 100;
  return Math.max(1, Math.floor((conquistadas / total) * 100));
}

/**
 * A data de conquista em dd/mm/aaaa, ou `null` se não houver.
 *
 * PARSEADA COM REGEX, e não com `new Date`. O servidor devolve
 * `"2026-08-01 10:00:00"` (o `(string) $card->obtained_at` do controller), e esse
 * formato — com espaço em vez de `T` — é inválido pela ECMA-262: o Chrome aceita
 * por tolerância e o Safari devolve `Invalid Date`. Como só se quer o dia, não há
 * razão para envolver fuso horário nenhum: o recorte é textual e dá o mesmo
 * resultado em todo navegador.
 */
export function dataDaConquista(valor) {
  if (!valor || typeof valor !== "string") return null;

  const partes = valor.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!partes) return null;

  return `${partes[3]}/${partes[2]}/${partes[1]}`;
}

/**
 * QUAL REGIÃO NASCE ABERTA quando a coleção aparece — a primeira em que o aluno tem
 * alguma carta e, se não tiver nenhuma em nenhuma, a primeira de todas.
 *
 * Existe porque o álbum passou a recolher por região (as grades são o que rola: 22
 * cartas por região, 110 nas cinco), e "todas abertas" devolve exatamente a rolagem
 * que o recolhimento veio resolver. Uma aberta mostra o gesto sem precisar explicá-lo:
 * o aluno vê uma grade e as vizinhas fechadas com o mesmo controle.
 *
 * A PRIMEIRA COM CARTA, e não a primeira: quem já jogou abre a página no álbum que
 * tem algo dentro, em vez de num painel inteiro de silhuetas — e quem nunca jogou vê
 * a primeira, que é onde o AVA o levaria de todo jeito.
 *
 * @param {Array} colecao de `colecaoPorRegiao`
 * @returns {string|null} o id da região, ou `null` sem região nenhuma
 */
export function regiaoAbertaPorPadrao(colecao = []) {
  if (colecao.length === 0) return null;

  const comCarta = colecao.find(regiao => (regiao.conquistadas || 0) > 0);
  return (comCarta || colecao[0]).id || null;
}
